// Deterministic shuffling shared with backend/internal/shared/exampkg/shuffle.go.
// The server publishes only a hex seed; the display order is derived here, so an
// auditor can rebuild it from (attempt_id, package checksum). Any change must be
// mirrored in the Go file and in the cross-language vectors of both test files.

/** First 32 bits (8 hex chars, big-endian) of the seed. */
export function seed32(seed: string): number {
  let value = 0
  for (let i = 0; i < 8 && i < seed.length; i++) {
    const nibble = parseInt(seed[i], 16)
    value = ((value << 4) | (Number.isNaN(nibble) ? 0 : nibble)) >>> 0
  }
  return value >>> 0
}

/** FNV-1a over the UTF-8 bytes of the text. */
export function fnv1a32(text: string): number {
  let hash = 2166136261
  for (const byte of new TextEncoder().encode(text)) {
    hash ^= byte
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return hash >>> 0
}

/** mulberry32: returns a generator of unsigned 32-bit integers. */
export function mulberry32(start: number): () => number {
  let state = start >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return (t ^ (t >>> 14)) >>> 0
  }
}

function permutation(n: number, next: () => number): number[] {
  const order = Array.from({ length: n }, (_, i) => i)
  for (let i = n - 1; i > 0; i--) {
    // next() * (i + 1) stays below 2^53, so the float arithmetic is exact.
    const j = Math.floor((next() * (i + 1)) / 4294967296)
    ;[order[i], order[j]] = [order[j], order[i]]
  }
  return order
}

/** Position p shows item index result[p]. */
export function shuffleItemOrder(n: number, seed: string): number[] {
  return permutation(n, mulberry32(seed32(seed)))
}

/** Option order for one item; independent stream per item id. */
export function shuffleOptionOrder(n: number, seed: string, itemId: string): number[] {
  return permutation(n, mulberry32((seed32(seed) ^ fnv1a32(itemId)) >>> 0))
}

/** Applies a permutation: result[p] = list[order[p]]. */
export function applyOrder<T>(list: readonly T[], order: readonly number[]): T[] {
  return order.map((index) => list[index])
}
